/**
 * Enemy UI Manager - 管理敌船的屏幕空间UI元素（名字和血条）
 */

import * as THREE from 'three';

export class EnemyUIManager {
    constructor(game) {
        this.game = game;
        this.enemyUIs = new Map(); // 存储每个敌船的UI元素

        // 创建UI容器
        this.createUIContainer();

        // 临时向量用于坐标转换
        this.tempVector = new THREE.Vector3();
    }

    createUIContainer() {
        // 创建敌船UI的容器元素
        this.uiContainer = document.createElement('div');
        this.uiContainer.id = 'enemy-ui-container';
        this.uiContainer.style.cssText = `
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            pointer-events: none;
            z-index: 100;
        `;

        document.body.appendChild(this.uiContainer);
    }

    /**
     * 为敌船创建UI元素
     */
    createEnemyUI(enemyId, enemyName, maxHealth) {
        if (this.enemyUIs.has(enemyId)) {
            return; // 已存在
        }

        // 创建敌船UI容器
        const enemyUIDiv = document.createElement('div');
        enemyUIDiv.className = 'enemy-ui';
        enemyUIDiv.style.cssText = `
            position: absolute;
            transform: translate3d(-9999px, -9999px, 0) translate(-50%, -100%);
            will-change: transform, opacity;
            pointer-events: none;
            opacity: 0;
            transition: opacity 0.3s ease;
            display: flex;
            flex-direction: column;
            align-items: center;
        `;

        // 创建名字标签
        const nameLabel = document.createElement('div');
        nameLabel.className = 'enemy-name';
        nameLabel.textContent = enemyName;
        nameLabel.style.cssText = `
            color: #FF5555;
            font-family: Arial, sans-serif;
            font-size: 14px;
            font-weight: bold;
            text-align: center;
            text-shadow: 2px 2px 4px rgba(0,0,0,0.8);
            margin-bottom: 4px;
            white-space: nowrap;
        `;

        // 创建血条容器
        const healthBarContainer = document.createElement('div');
        healthBarContainer.className = 'health-bar-container';
        healthBarContainer.style.cssText = `
            width: 60px;
            height: 8px;
            background: rgba(0,0,0,0.6);
            border: 1px solid rgba(255,255,255,0.3);
            border-radius: 4px;
            overflow: hidden;
            position: relative;
            margin: 0 auto;
        `;

        // 创建血条
        const healthBar = document.createElement('div');
        healthBar.className = 'health-bar';
        healthBar.style.cssText = `
            width: 100%;
            height: 100%;
            background: linear-gradient(90deg, #FF4444, #FF6666);
            transition: width 0.3s ease;
            border-radius: 3px;
        `;

        // 创建血条分段显示
        const healthSegments = document.createElement('div');
        healthSegments.className = 'health-segments';
        healthSegments.style.cssText = `
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            display: flex;
            pointer-events: none;
        `;

        // 创建血条分段 - 固定为6段血条
        for (let i = 0; i < 6; i++) {
            const segment = document.createElement('div');
            segment.style.cssText = `
                flex: 1;
                ${i < 5 ? 'border-right: 1px solid rgba(0,0,0,0.4);' : ''}
                height: 100%;
            `;
            healthSegments.appendChild(segment);
        }

        // 组装UI结构
        healthBarContainer.appendChild(healthBar);
        healthBarContainer.appendChild(healthSegments);
        enemyUIDiv.appendChild(nameLabel);
        enemyUIDiv.appendChild(healthBarContainer);
        this.uiContainer.appendChild(enemyUIDiv);

        // 存储UI元素引用
        this.enemyUIs.set(enemyId, {
            container: enemyUIDiv,
            nameLabel: nameLabel,
            healthBar: healthBar,
            healthBarContainer: healthBarContainer,
            maxHealth: Math.max(1, maxHealth || 1),
            isVisible: false,
            lastX: null,
            lastY: null,
            lastHealthPercent: null,
            lastHealthZone: null
        });
    }

    /**
     * 更新敌船UI位置和状态
     */
    updateEnemyUI(enemyId, worldPosition, health, maxHealth) {
        const ui = this.enemyUIs.get(enemyId);
        if (!ui) return;

        // 将3D世界坐标转换为2D屏幕坐标
        const screenPos = this.worldToScreen(worldPosition);

        // 检查是否在屏幕内
        const isOnScreen = screenPos.x >= 0 && screenPos.x <= window.innerWidth &&
                          screenPos.y >= 0 && screenPos.y <= window.innerHeight &&
                          screenPos.z > 0 && screenPos.z < 1;

        if (isOnScreen) {
            const offsetX = Math.round(screenPos.x);
            const offsetY = Math.round(screenPos.y - 30);
            if (ui.lastX !== offsetX || ui.lastY !== offsetY) {
                ui.container.style.transform = `translate3d(${offsetX}px, ${offsetY}px, 0) translate(-50%, -100%)`;
                ui.lastX = offsetX;
                ui.lastY = offsetY;
            }

            if (!ui.isVisible) {
                ui.container.style.opacity = '1';
                ui.isVisible = true;
            }

            const maxHp = Math.max(1, (maxHealth ?? ui.maxHealth) || 1);
            if (ui.maxHealth !== maxHp) {
                ui.maxHealth = maxHp;
                ui.lastHealthPercent = null;
            }

            const clampedHealth = Math.max(0, Math.min(maxHp, health ?? 0));
            const healthPercent = Math.round(((clampedHealth / maxHp) * 100) * 10) / 10;

            if (ui.lastHealthPercent === null || Math.abs(ui.lastHealthPercent - healthPercent) >= 0.5) {
                ui.healthBar.style.width = `${healthPercent}%`;
                ui.lastHealthPercent = healthPercent;
            }

            let zone = 'high';
            if (healthPercent <= 30) {
                zone = 'low';
            } else if (healthPercent <= 60) {
                zone = 'mid';
            }

            if (ui.lastHealthZone !== zone) {
                const background = zone === 'low'
                    ? 'linear-gradient(90deg, #FF2222, #FF4444)'
                    : zone === 'mid'
                        ? 'linear-gradient(90deg, #FF4444, #FF6666)'
                        : 'linear-gradient(90deg, #FF4444, #FF6666)';
                ui.healthBar.style.background = background;
                ui.lastHealthZone = zone;
            }

        } else {
            // 隐藏UI
            if (ui.isVisible) {
                ui.container.style.opacity = '0';
                ui.isVisible = false;
                ui.lastX = null;
                ui.lastY = null;
            }
        }
    }

    /**
     * 移除敌船UI
     */
    removeEnemyUI(enemyId) {
        const ui = this.enemyUIs.get(enemyId);
        if (ui) {
            this.uiContainer.removeChild(ui.container);
            this.enemyUIs.delete(enemyId);
        }
    }

    /**
     * 将3D世界坐标转换为2D屏幕坐标
     */
    worldToScreen(worldPosition) {
        if (!this.game.sceneManager.camera) {
            return { x: 0, y: 0, z: 1 };
        }

        // 复制世界位置并添加一点高度偏移
        this.tempVector.copy(worldPosition);
        this.tempVector.y += 3; // 在敌船上方显示

        // 投影到屏幕坐标
        this.tempVector.project(this.game.sceneManager.camera);

        // 转换为屏幕像素坐标
        const screenX = (this.tempVector.x * 0.5 + 0.5) * window.innerWidth;
        const screenY = (-this.tempVector.y * 0.5 + 0.5) * window.innerHeight;

        return {
            x: screenX,
            y: screenY,
            z: this.tempVector.z
        };
    }

    /**
     * 计算到摄像机的距离
     */
    getDistanceToCamera(worldPosition) {
        if (!this.game.sceneManager.camera) {
            return 50;
        }

        return this.game.sceneManager.camera.position.distanceTo(worldPosition);
    }

    /**
     * 更新所有敌船UI（从游戏主循环调用）
     */
    update() {
        // 这个方法会在EnemyShip.update中被调用
        // 这里可以处理全局的UI更新逻辑
    }

    /**
     * 清理所有UI元素
     */
    dispose() {
        if (this.uiContainer && this.uiContainer.parentNode) {
            this.uiContainer.parentNode.removeChild(this.uiContainer);
        }
        this.enemyUIs.clear();
    }
}
